import { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Store, Receipt, ReceiptText, FileClock, Star, History, Bell, Banknote, BookOpenCheck, Wallet, LogOut, PanelLeftClose, PanelLeftOpen, Lamp, Coffee } from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import BoarMark from './BoarMark';
import NotificationsBell from './NotificationsBell';
import { api } from '../api/sangly';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { cn } from '../lib/utils';

// `module` marks an entry as gated by a Virgy module (a page HR can restrict
// per employee — see users/access.py). Entries without one are always shown.
// `payers` hides an entry from users who may not execute payments (see visibleNav).
// `badge` names the key in /api/todo/counts/ whose open-item count shows next
// to the label (so a queue with work in it is visible from anywhere).
// Groups render as separate blocks in the sidebar so the day-to-day pages
// aren't buried under the personal ones (following / history / notifications).
// One legal entity, two businesses: the LampTwist block is the full workspace,
// the Calum block shows the same Suppliers / Invoices pages scoped to the
// Calum Brussels bills (split by the accounts on the bill lines, see
// backend/invoices/business_unit.py).
// `icon` (or `logo`, an image URL) marks the group heading. Placeholder line
// icons until the real LampTwist / Calum logos are dropped in.
const NAV = [
  { group: 'LampTwist', icon: Lamp, items: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/suppliers', label: 'Suppliers', icon: Store },
    { to: '/invoices', label: 'Invoices', icon: Receipt },
    { to: '/expense-notes', label: 'Expense Notes', icon: ReceiptText, badge: 'expense_notes' },
    { to: '/proformas', label: 'Pro-forma', icon: FileClock, badge: 'proformas' },
    { to: '/refunds', label: 'Refund Mgmt', icon: Banknote, badge: 'refunds' },
    { to: '/encodage', label: 'Encodage', icon: BookOpenCheck, module: 'encodage' },
    { to: '/paypal', label: 'PayPal', icon: Wallet, payers: true },
  ] },
  { group: 'Calum', icon: Coffee, items: [
    { to: '/calum/suppliers', label: 'Suppliers', icon: Store },
    { to: '/calum/invoices', label: 'Invoices', icon: Receipt },
  ] },
  { group: 'Activity', items: [
    { to: '/following', label: 'Following', icon: Star },
    { to: '/history', label: 'History', icon: History },
    { to: '/notifications', label: 'Notifications', icon: Bell },
  ] },
];

// Hide only what the server says is off-limits: a page absent from `modules`
// isn't restricted, and `=== false` keeps a stale cached user (no field yet)
// from losing entries until /me refreshes.
// `payers` marks a page only the designated payers (SANGLY_PAYERS) can use -
// the endpoint answers 403 to everyone else, so the entry stays hidden for them.
const visibleNav = (user) =>
  NAV.map(({ items, ...rest }) => ({
    ...rest,
    items: items.filter(({ module, payers }) =>
      (!module || user?.modules?.[module] !== false) && (!payers || !!user?.can_pay)),
  })).filter(({ items }) => items.length > 0);

// Group heading marker: a logo image when one is set, else the line icon.
function GroupMark({ icon: Icon, logo, className }) {
  if (logo) return <img src={logo} alt="" className={cn('rounded-sm object-contain', className)} />;
  if (Icon) return <Icon className={className} aria-hidden />;
  return null;
}

export default function AppLayout({ title, description, actions, syncBar, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [counts, setCounts] = useState({});
  const initials = (name) => (name ? name.slice(0, 2).toUpperCase() : 'U');

  // Open-item counts for the badges: refreshed on every page change (so a
  // "mark paid" shows up as soon as you navigate) plus a gentle poll.
  const loadCounts = useCallback(async () => {
    try { setCounts(await api.todo.counts()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts, location.pathname]);
  useEffect(() => {
    const t = setInterval(loadCounts, 60000);
    return () => clearInterval(t);
  }, [loadCounts]);

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <aside className={cn(
        'z-30 flex border-b border-border bg-card/90 backdrop-blur transition-all duration-200 lg:sticky lg:top-0 lg:h-screen lg:flex-col lg:justify-between lg:border-b-0 lg:border-r',
        collapsed ? 'lg:w-[60px]' : 'lg:w-[228px]'
      )}>
        <div className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 lg:block lg:px-3 lg:py-4">
          {/* the boar hides a game: clicking the logo sneaks off to pétanque */}
          <button onClick={() => navigate('/petanque')} title="Sangly"
                  className="flex h-9 shrink-0 items-center gap-2.5 px-1 lg:w-full">
            <BoarMark size={34} className="shrink-0 rounded-lg" rounded={12} />
            {!collapsed && <span className="hidden text-[16px] font-semibold tracking-tight lg:inline">Sangly</span>}
          </button>

          <nav className="flex min-w-0 flex-1 items-center justify-center gap-1 overflow-x-auto lg:mt-6 lg:block lg:space-y-5 lg:overflow-visible">
            {visibleNav(user).map(({ group, icon, logo, items }, i) => (
              <div key={group} className="flex shrink-0 items-center gap-1 lg:block lg:space-y-0.5">
                {/* mobile: a thin separator between groups; desktop: a heading, or a rule when collapsed */}
                {i > 0 && <span aria-hidden className="mx-0.5 h-5 w-px shrink-0 bg-border lg:hidden" />}
                {collapsed
                  ? (icon || logo)
                    ? <div className="mb-1 hidden justify-center text-muted-foreground lg:flex" title={group}><GroupMark icon={icon} logo={logo} className="h-3.5 w-3.5" /></div>
                    : i > 0 && <span aria-hidden className="mx-2 mb-2 hidden h-px bg-border lg:block" />
                  : <p className="mb-1 hidden items-center gap-1.5 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground lg:flex">
                      <GroupMark icon={icon} logo={logo} className="h-3.5 w-3.5" />{group}
                    </p>}
                {items.map(({ to, label, icon: Icon, badge }) => {
                  const n = badge ? counts[badge] || 0 : 0;
                  return (
                  <NavLink key={to} to={to} title={n ? `${label} · ${n} to do` : label}
                    className={({ isActive }) => cn(
                      'group relative flex shrink-0 items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium transition-colors sm:px-3 lg:gap-2.5 lg:px-2.5',
                      isActive ? 'bg-primary/12 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      collapsed && 'lg:justify-center lg:px-0'
                    )}>
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute bottom-0 left-2 right-2 h-[2px] rounded-t-full bg-primary lg:bottom-auto lg:left-0 lg:right-auto lg:top-1.5 lg:h-auto lg:w-[3px] lg:rounded-r-full" />}
                        <span className="relative shrink-0">
                          <Icon className="h-[18px] w-[18px]" />
                          {/* collapsed sidebar + mobile: a dot is all there's room for */}
                          {n > 0 && (
                            <span className={cn('absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary ring-2 ring-card', !collapsed && 'lg:hidden')} />
                          )}
                        </span>
                        {!collapsed && <span className="hidden lg:inline">{label}</span>}
                        {!collapsed && n > 0 && (
                          <span className="ml-auto hidden rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary lg:inline">
                            {n}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-1 px-2 py-2 sm:px-3 lg:block lg:px-3 lg:pb-4">
          <button onClick={() => setCollapsed((c) => !c)}
            className={cn('mb-2 hidden h-8 w-full items-center gap-2 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-muted lg:flex', collapsed && 'justify-center px-0')}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /> Collapse</>}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn('flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-muted', collapsed && 'justify-center px-0')}>
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.clerkImageUrl || user?.profile?.avatar} alt={user?.displayName} />
                  <AvatarFallback className="text-[11px]">{initials(user?.displayName)}</AvatarFallback>
                </Avatar>
                {!collapsed && <span className="hidden truncate text-sm lg:inline">{user?.displayName || 'Account'}</span>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="truncate">{user?.displayName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => logout()} className="gap-2">
                <LogOut className="h-4 w-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-md sm:px-6">
          <div className="min-w-0">
            <h1 className="truncate text-[17px] font-semibold tracking-tight">{title}</h1>
            {description && <p className="hidden truncate text-[13px] text-muted-foreground sm:block">{description}</p>}
          </div>
          <div className="flex min-w-0 shrink-0 items-center gap-2.5">
            {syncBar}
            {actions}
            <NotificationsBell />
          </div>
        </header>
        <div className="flex-1 px-4 py-4 sm:px-6 sm:py-6">{children}</div>
      </main>
    </div>
  );
}
